import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { AxiosError } from "axios";

import { IEventMessage, IEventSummary } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import { apiService } from "src/services/api.service";
import { useDebounce } from "src/hooks/useDebounce";

import { Modal, useModal } from "src/components/Modal/Modal";
import Input from "src/components/Input/Input";
import Button from "src/components/Button/Button";
import ContextMenu, { IContextMenuOption } from "src/components/ContextMenu/ContextMenu";
import SendMessage, { SendMessageType } from "src/components/SendMessage/SendMessage";
import { download } from "src/components/Download/DownloadManager";
import { toast } from "src/components/Toast/ToastManager";

import { type ColDef, GridApi, type TextFilterModel, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { ActionsRenderer } from "./ActionsRenderer";

import styles from "./RegistrantsTab.module.scss";

interface IProps {
  event: IEventSummary,
  currentTab: number,
  tabIndex: number
}

interface IRow {
  callName: string,
  armband: string,
  breed: string,
  ownerName: string,
  ownerEmail: string,
  show: string,
  showDate: string,
  registeredAt: string,
  actions: any
}

interface IFilters {
  ownerName?: TextFilterModel
  ownerEmail?: TextFilterModel
}

const RegistrantsTab: React.FC<IProps> = (props) => {
  const [loaded, setLoaded] = useState<boolean>(true);
  const [isSending, setIsSending] = useState<boolean>(false);

  const [gridApi, setGridApi] = useState<GridApi<IRow>>();

  const [ownerNameSearch, setOwnerNameSearch] = useState<string>("");
  const debouncedOwnerNameSearch = useDebounce<string>(ownerNameSearch, 800);

  const [emailSearch, setEmailSearch] = useState<string>("");
  const debouncedEmailSearch = useDebounce<string>(emailSearch, 800);

  const { isShown, toggle } = useModal();

  let menuActions:IContextMenuOption[] = [
    {
      label: "Export Registrants",
      onClick: () => {
        download.pushDownload({
          type: "armband",
          eventId: props.event._id
        });
      }
    },
    {
      label: "Export Registrations",
      onClick: () => {
        download.pushDownload({
          type: "scores",
          eventId: props.event._id
        });
      }
    },
    {
      label: "Email Registrants",
      onClick: () => {
        toggle();
      }
    }
  ];

  if (props.event.sanctioning_club !== Constants.sanctioning_club.PWDCA) {
    // @ts-expect-error toSpliced exists on []
    menuActions = menuActions.toSpliced(2, 0, {
      label: "Download Forms",
      onClick: () => {
        download.pushDownload({
          type: "print_registration",
          eventId: props.event._id
        });
      }
    })
  }

  const theme = themeQuartz.withParams({
    fontFamily: "Roboto",
    headerFontFamily: "Roboto",
    cellFontFamily: "Roboto"
  });

  const onGridReady = (params: any) => {
    setGridApi(params.api);
  }

  const resetFilters = useCallback(() => {
    if (gridApi) {
      setOwnerNameSearch("");
      setEmailSearch("");
      gridApi.setFilterModel(null);
    }
  }, [gridApi]);

  const getRegistrants = useCallback((id: string) => {
    let query:any = {
      sort: [{
        attribute_name: "show_type",
        sort: "asc"
      }, {
        attribute_name: "created_at",
        sort: "asc"
      }],
    };

    apiService.getEventShowRegistrations(id, query).then((response) => {
      let rows:IRow[] = [];

      for (let i in response) {
        let row:any = response[i];

        rows.push({
          callName: row.dog.call_name + " (" + row.dog.registered_name + ")",
          armband: row.arm_number,
          breed: row.dog.breed,
          ownerName: row.owner.display_name,
          ownerEmail: row.owner.email,
          show: row.show_type + " " + row.show_name,
          showDate: moment(row.show_date).format("MM/DD/YY"),
          registeredAt: moment(row.created_at).format("MM/DD/YY hh:mm:ssA"),
          actions: {
            registration: row,
            event: props.event
          }
        });
      }

      setRowData(rows);
    });
  }, [props.event]);

  let colDefs:ColDef<IRow>[] = [
    { headerName: "Call Name (Reg. Name)", field: "callName", minWidth: 150, filter:true },
    { field: "armband", minWidth: 120, maxWidth: 120, cellDataType: "text", filter:true },
    { field: "breed", minWidth: 150, filter:true },
    { field: "ownerName", minWidth: 150, cellDataType: "text", filter: "agTextColumnFilter" },
    { field: "ownerEmail", minWidth: 150, cellDataType: "text", filter: "agTextColumnFilter" },
    { field: "show", minWidth: 150, filter:true },
    { field: "showDate", minWidth: 150, filter:true },
    { field: "registeredAt", minWidth: 150, filter:true },
    {
      headerName: "",
      field: "actions",
      maxWidth: 50,
      minWidth: 50,
      cellRenderer: ActionsRenderer,
      pinned: "right",
      cellRendererParams: {
        getRegistrants: () => { getRegistrants(props.event._id); }
      },
      resizable: false,
      cellStyle: {
        "overflow": "visible",
        "zIndex": "2"
      }
    }
  ];

  const [rowData, setRowData] = useState<IRow[]>([]);

  const defaultColDef: ColDef = {
    flex: 1
  };

  const emailRegistrants = (payload: IEventMessage) => {
    setIsSending(true)
    const requests = [
      apiService.requestSendMessageEmailForEvent(props.event._id, payload),
    ];
    if (payload.add_armbands) {
      requests.push(apiService.generatedArmNumbersPerShowTypeEmails(props.event._id));
    }

    const content = payload.is_test_sending ? `Test email sent to ${props.event.club.email}.` : "Your message has been sent to all current registrants."

    Promise.all(requests)
      .then((values) => {
        toast.show({
          title: "Email Results",
          content,
          duration: 10000,
          type: "success"
        });
      })
      .catch((e: AxiosError) => {
        toast.show({
          title: "Email Results",
          content: "Unable to email results",
          duration: 10000,
          errorDetails: e,
          type: "fail"
        });
      })
      .finally(() => {
        setIsSending(false)
        toggle();
      });
  }

  useEffect(() => {
    if (props.currentTab === props.tabIndex) {
      getRegistrants(props.event._id);
    }
  }, [props.currentTab, props.tabIndex, props.event._id, getRegistrants]);

  useEffect(() => {
    if (!loaded) {
      getRegistrants(props.event._id);
      setLoaded(true);
    }
  }, [loaded, getRegistrants, props.event._id]);

  useEffect(() => {
    let filters: IFilters = {};

    if (gridApi) {
      if (!!debouncedEmailSearch) {
        filters.ownerEmail = {
          filterType: "text",
          type: "contains",
          filter: debouncedEmailSearch,
        };
      } else {
        filters.ownerEmail = undefined;
      }

      if (!!debouncedOwnerNameSearch) {
        filters.ownerName = {
          filterType: "text",
          type: "contains",
          filter: debouncedOwnerNameSearch,
        };
      } else {
        filters.ownerName = undefined;
      }

      gridApi.setFilterModel(filters)
    }
  }, [gridApi, debouncedOwnerNameSearch, debouncedEmailSearch]);

  return (
    <div className={styles.RegistrantsTab}>
      <div className={styles.actions}>
        <div className={styles.left}>
          <Input
            className={styles.textInput}
            autoComplete="off"
            type="text"
            label="Owner Name"
            value={ownerNameSearch}
            onChange={(e) => { setOwnerNameSearch(e.target.value); }}
          />
          <Input
            className={styles.textInput}
            autoComplete="off"
            type="email"
            label="Owner Email"
            value={emailSearch}
            onChange={(e) => { setEmailSearch(e.target.value); }}
          />
          <Button disabled={!(ownerNameSearch || emailSearch)} className={styles.reset} onClick={() => { resetFilters(); }}>Reset</Button>
        </div>
        <div className={styles.right}>
          <ContextMenu
            label="Actions"
            options={menuActions}
          />
        </div>
      </div>
      <div className={styles.tableWrap}>
      <AgGridReact
        suppressRowTransform
        pagination
        onGridReady={onGridReady}
        rowData={rowData}
        columnDefs={colDefs}
        defaultColDef={defaultColDef}
        theme={theme}
        paginationPageSize={20}
      />
      </div>
      <Modal
        isShown={isShown}
        hide={() => { toggle(); }}
        className={"sendMessageModal"}
        modalContent={
          <SendMessage
            isSending={isSending}
            handleSendMessage={emailRegistrants}
            subject={`New Message from Organizer for ${props.event.title}`}
            type={SendMessageType.Registrants}
          />
        }
      />
    </div>
  );
};

export default RegistrantsTab;
